/**
 * Cascading category picker: one dropdown per level (Category, Sub-category,
 * Item). Choosing a node at one level reveals the next level's dropdown when
 * that node has children. Reports the deepest node chosen so far.
 *
 * Used for the queue's category filter (any level counts) and for changing a
 * ticket's category (the ticket page only saves once a leaf is chosen).
 */

import type { SdCategoryAdminNode } from '@/lib/serviceDeskTypes';
import { SD_INPUT } from './sdStyles';
import { pathTo } from './categoryTree';

const LEVEL_NAMES = ['category', 'sub-category', 'item'];

type Level = {
  options: SdCategoryAdminNode[];
  selected: SdCategoryAdminNode | undefined;
  parent: SdCategoryAdminNode | null;
};

export function CategoryPicker({
  tree,
  value,
  onChange,
  anyLabel,
  idPrefix,
}: {
  tree: SdCategoryAdminNode[];
  value: number | null;
  onChange: (id: number | null, node: SdCategoryAdminNode | null) => void;
  /** When set, the first level starts with this blank option (for example "Any category"). */
  anyLabel?: string;
  idPrefix: string;
}) {
  const chosen = pathTo(tree, value);
  const levels: Level[] = [];
  let options = tree;
  let parent: SdCategoryAdminNode | null = null;
  for (let depth = 0; depth < 3 && options.length > 0; depth += 1) {
    const selected: SdCategoryAdminNode | undefined = chosen[depth];
    levels.push({ options, selected, parent });
    if (!selected) break;
    parent = selected;
    options = selected.children;
  }

  return (
    <div className="flex flex-col gap-2">
      {levels.map((lvl, depth) => {
        const noun = LEVEL_NAMES[depth] ?? 'category';
        const blank = anyLabel ? (depth === 0 ? anyLabel : `Any ${noun}`) : `Choose a ${noun}`;
        return (
          <select
            key={depth}
            id={`${idPrefix}-${depth}`}
            aria-label={noun.charAt(0).toUpperCase() + noun.slice(1)}
            value={lvl.selected?.id ?? ''}
            onChange={(e) => {
              if (e.target.value === '') {
                // Back out to the parent level (or to no choice at the top).
                onChange(lvl.parent ? lvl.parent.id : null, lvl.parent);
                return;
              }
              const id = Number(e.target.value);
              onChange(id, lvl.options.find((o) => o.id === id) ?? null);
            }}
            className={SD_INPUT}
          >
            <option value="">{blank}</option>
            {lvl.options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        );
      })}
    </div>
  );
}
